//
// Help engine. Owns the single driver.js player every help surface goes through:
// the welcome step-through on first login, the centred page introduction on first
// arrival, the first-use explanation an action wrapped with explainAction shows
// before running, the explanation behind an explain-mode marker, the on-demand
// tours and the release notes. Status persists in settings.user.onboarding
// (explained, tutos, skip, releaseSeen). debugAlwaysReplay keeps everything in
// session only so it replays on reload.
//

import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import '../../assets/css/tutorial.css';
import explainRegistry from '../../tutorials/explainRegistry';
import releaseHighlights from '../../tutorials/releaseHighlights';
import releaseNotesHtml from '../../tutorials/releaseNotesHtml';
import tutorialFeatureEnabled from '../../tutorials/tutorialFeatureEnabled';
import tutorialRegistry from '../../tutorials/tutorialRegistry';
import usePrivileges from '../../utils/hooks/usePrivileges';
import useAuth from '../auth/useAuth';
import useTownAssets from '../townAssets/useTownAssets';

import TutorialContext from './TutorialContext';

const findSection = (id) => tutorialRegistry.find((section) => section.id === id);

// How long to wait (×100ms) for a section's trigger to mount after navigating to
// its page for a replay before giving up.
const triggerWaitTries = 20;

// A page's introduction fires shortly after arrival, so the lazy page has mounted
// and a route redirect has settled before the popup opens.
const arrivalDelayMs = 400;

// The first explanations a user meets carry no opt-out: with fewer than this
// many seen, nobody can judge whether they want the rest.
const optOutAfterExplanations = 2;

// Debug switch: when true nothing is persisted, so everything replays on reload.
const debugAlwaysReplay = false;

const latestReleaseDate = releaseHighlights[0]?.date;

const isVisible = (selector) => {
  if (!selector) return false;
  const element = document.querySelector(selector);
  return Boolean(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));
};

// Where a record hangs off the side menu: its first entry currently on screen.
const menuAnchor = (record) => (record.menuSelector ?? []).find((selector) => isVisible(selector));

// The page record matching a path: the longest `page` the path starts with.
const pageRecordFor = (pathname) => Object.entries(explainRegistry)
  .filter(([, record]) => (record.scope === 'page') && pathname.startsWith(`/${record.page}`))
  .sort(([, a], [, b]) => b.page.length - a.page.length)
  .map(([id, record]) => ({ id, ...record }))[0];

// A React event's currentTarget is nulled once dispatch ends; an action deferred
// until the explanation closes needs the fields it read at click time.
const freezeArg = (arg) => (
  ((arg !== null) && (typeof arg === 'object') && ('currentTarget' in arg)) ? { ...arg } : arg
);

function TutorialProvider({ children }) {
  const { auth } = useAuth();
  const { info, addUserSetting } = auth;
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { privilegesReady, hasAnyPrivilege } = usePrivileges();
  const navigate = useNavigate();
  const location = useLocation();

  const privlg = info?.privlg;
  const onboarding = useMemo(
    () => info?.settings?.user?.onboarding ?? {},
    [info],
  );

  // Session mirrors of the persisted status. Refs are read synchronously by the
  // play chain (state updates are async and would re-pick the same record).
  const [sessionDone, setSessionDone] = useState(() => new Set());
  const [sessionExplained, setSessionExplained] = useState(() => new Set());
  const [skip, setSkipState] = useState(undefined);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  const [explainOn, setExplainOn] = useState(false);
  // What the player is showing: a multi-step 'tour' or a 'single' explanation.
  const [busyKind, setBusyKind] = useState(null);
  const doneRef = useRef(new Set());
  const explainedRef = useRef(new Set());
  const skipRef = useRef(undefined);
  const releaseSeenRef = useRef(undefined);
  const explainOnRef = useRef(false);
  // Onboarding lands with auth: read before that and an empty one reads as a
  // profile that has never been shown anything.
  const onboardingLoadedRef = useRef(false);
  // The status this session has written: a settings refresh landing while a stamp is
  // in flight would otherwise hand the next write a base missing what was just stamped.
  const persistedOnboardingRef = useRef(null);
  const busyRef = useRef(false);
  const currentDriverRef = useRef(null);
  const pendingPlayRef = useRef(null);
  const arrivalTimerRef = useRef(null);
  const autoOfferedRef = useRef(new Set());
  const apiRef = useRef({});

  useEffect(
    () => {
      if (!info) return;
      if (skipRef.current === undefined) {
        skipRef.current = onboarding.skip ?? false;
        setSkipState(onboarding.skip ?? false);
      }
      releaseSeenRef.current ??= (onboarding.releaseSeen ?? null);
      onboardingLoadedRef.current = true;
      setOnboardingLoaded(true);
    },
    [info, onboarding],
  );

  const roleAllowed = useCallback(
    (roles) => (!roles?.length) || roles.some((role) => (privlg ?? []).includes(role)),
    [privlg],
  );
  // Paxity's own accounts reach a back office to work in it, not to be taught it:
  // nothing plays unasked for them, but every help surface stays reachable on demand.
  const paxityAccount = (privlg ?? []).includes('super');
  const paxityAccountRef = useRef(paxityAccount);
  paxityAccountRef.current = paxityAccount;
  const eligible = useCallback(
    (record) => roleAllowed(record.roles)
      && hasAnyPrivilege(record.requires)
      && tutorialFeatureEnabled(record.featureFlag),
    [roleAllowed, hasAnyPrivilege],
  );
  // The base a change merges into: this session's own status once it has written.
  const currentOnboarding = useCallback(
    () => (persistedOnboardingRef.current ?? onboarding),
    [onboarding],
  );

  const isDone = useCallback(
    (section) => doneRef.current.has(section.id)
      || ((currentOnboarding().tutos?.[section.id] ?? 0) >= section.version),
    [currentOnboarding],
  );
  // The version the user last completed: what a growing section replays from.
  const versionSeen = useCallback(
    (section) => (currentOnboarding().tutos?.[section.id] ?? 0),
    [currentOnboarding],
  );
  const isExplained = useCallback(
    (id) => explainedRef.current.has(id)
      || ((currentOnboarding().explained?.[id] ?? 0) >= (explainRegistry[id]?.version ?? 1)),
    [currentOnboarding],
  );

  const persistOnboarding = useCallback(
    (changes) => {
      if (debugAlwaysReplay) return;
      const next = {
        ...currentOnboarding(),
        ...changes,
      };
      Object.keys(next).forEach((key) => {
        if (next[key] === undefined) delete next[key];
      });
      persistedOnboardingRef.current = next;
      addUserSetting({ onboarding: next });
    },
    [currentOnboarding, addUserSetting],
  );

  // A profile with no release stamp at all is opening the product for the first
  // time: its backlog is the whole thing, so the announcements are stamped read,
  // never shown. An empty stamp is the opposite — core-town level 80 gives one to
  // every staff account predating the announcements, and they are owed the backlog.
  useEffect(
    () => {
      if ((!onboardingLoaded) || (!latestReleaseDate) || (releaseSeenRef.current !== null)) return;
      releaseSeenRef.current = latestReleaseDate;
      persistOnboarding({ releaseSeen: latestReleaseDate });
    },
    [onboardingLoaded, persistOnboarding],
  );

  const markDone = useCallback(
    (section) => {
      doneRef.current.add(section.id);
      setSessionDone((prev) => new Set(prev).add(section.id));
      persistOnboarding({
        tutos: {
          ...(currentOnboarding().tutos ?? {}),
          [section.id]: section.version,
        },
      });
    },
    [currentOnboarding, persistOnboarding],
  );

  const markExplained = useCallback(
    (id) => {
      if (explainedRef.current.has(id)) return;
      explainedRef.current.add(id);
      setSessionExplained((prev) => new Set(prev).add(id));
      persistOnboarding({
        explained: {
          ...(currentOnboarding().explained ?? {}),
          [id]: explainRegistry[id]?.version ?? 1,
        },
      });
    },
    [currentOnboarding, persistOnboarding],
  );

  // Walking a record inside a tour is seeing it: the same record also gates a
  // control's first use, which must not explain it a second time. Stamped together
  // when the tour ends, rather than a settings write per step.
  const markExplainedSeen = useCallback(
    (ids) => {
      const fresh = ids.filter(
        (id) => explainRegistry[id] && (explainRegistry[id].scope !== 'tour') && (!explainedRef.current.has(id)),
      );
      if (!fresh.length) return;
      fresh.forEach((id) => explainedRef.current.add(id));
      setSessionExplained((prev) => {
        const next = new Set(prev);
        fresh.forEach((id) => next.add(id));
        return next;
      });
      const explained = { ...(currentOnboarding().explained ?? {}) };
      fresh.forEach((id) => {
        explained[id] = explainRegistry[id].version ?? 1;
      });
      persistOnboarding({ explained });
    },
    [currentOnboarding, persistOnboarding],
  );

  const setSkip = useCallback(
    (value) => {
      skipRef.current = value;
      setSkipState(value);
      persistOnboarding({ skip: value });
    },
    [persistOnboarding],
  );

  // Dev/demo only: back to a first login — everything reads as new, auto-explain
  // is on again, and the welcome plays right away.
  const reset = useCallback(
    () => {
      doneRef.current = new Set();
      explainedRef.current = new Set();
      setSessionDone(new Set());
      setSessionExplained(new Set());
      releaseSeenRef.current = null;
      autoOfferedRef.current = new Set();
      skipRef.current = false;
      setSkipState(false);
      persistOnboarding({
        tutos: undefined,
        explained: undefined,
        releaseSeen: undefined,
        skip: false,
      });
      clearTimeout(arrivalTimerRef.current);
      arrivalTimerRef.current = setTimeout(() => apiRef.current.runArrivals(), arrivalDelayMs);
    },
    [persistOnboarding],
  );

  const recordTitle = useCallback(
    (record) => translate(record.titleKey, 'tutorial'),
    [translate],
  );
  // What the step is waiting for, set apart from the prose: a step that asks for a
  // real click competed with its own footer button, and people pressed the button.
  const actionStrip = useCallback(
    (actionKey, targetKey, arrow) => {
      if (!actionKey) return '';
      // An arrow ahead of the text points the way to a control off the popover's side.
      const lead = arrow ? `<img class="px-tutorial-action-arrow" src="/images/svg/gui/arrow${arrow}.svg" alt="">` : '';
      // The control is named as it looks in the side menu, so the eye can match the
      // instruction to the thing it has to click.
      // The hand hangs off the tile's lower corner, pointing at it.
      const target = targetKey
        ? '<span class="px-tutorial-tile-wrap">'
          + `<span class="px-tutorial-menu-tile">${translate(targetKey, 'tutorial')}</span>`
          + '<img class="px-tutorial-hand" src="/images/svg/gui/pointingHand.svg" alt="">'
          + '</span>'
        : undefined;
      return `<p class="px-tutorial-action">${lead}${
        translate(actionKey, 'tutorial', target ? { target } : undefined, false)}</p>`;
    },
    [translate],
  );

  // A body written as several sentences carries newlines, and driver renders the
  // description as HTML, which would run them together; lines opening with `* `
  // are a list and would otherwise show their marker.
  const recordBody = useCallback(
    (record) => {
      if (record.descriptionHtml) return record.descriptionHtml(translate);
      const text = translate(record.bodyKey, 'tutorial');
      if (!text.includes('\n')) return text;
      return text.split('\n').reduce(
        (html, line) => {
          const bullet = line.startsWith('* ') ? line.slice(2) : null;
          if (!bullet) return `${html}<p>${line}</p>`;
          // Keep consecutive bullets inside the one list.
          return html.endsWith('</li></ul>')
            ? `${html.slice(0, -5)}<li>${bullet}</li></ul>`
            : `${html}<ul class="px-tutorial-list"><li>${bullet}</li></ul>`;
        },
        '',
      );
    },
    [translate],
  );

  // sinceVersion > 0 keeps only the steps added later, behind a heads-up step.
  const buildSteps = useCallback(
    (section, sinceVersion = 0) => {
      // driver renders the popover title via innerHTML, so the title carries the
      // tutorial name (styled eyebrow) above the step title.
      const guided = section.steps.some((step) => step.advanceOnClick);
      const sectionName = translate(section.labelKey, 'tutorial');
      const eyebrow = (title) => `<span class="px-tutorial-name">${sectionName}</span>`
        + `<span class="px-tutorial-step">${title}</span>`;
      const steps = section.steps
        .map((step) => ({
          ...step,
          record: explainRegistry[step.item],
        }))
        .filter((step) => step.record && eligible(step.record))
        .filter((step) => ((step.version ?? 1) > sinceVersion))
        .map((step) => ({
          ...step,
          selector: step.selector ?? step.record.selector,
        }))
        // A guided sequence creates its own anchors as it goes: the menu it asks the
        // user to open is shut when the tour is built, so none of its steps can be
        // dropped for a missing anchor.
        .filter((step) => guided || (!step.selector) || isVisible(step.selector))
        .filter((step) => (!step.showWhenAbsent) || (!isVisible(step.showWhenAbsent)))
        .map((step) => ({
          ...(step.selector ? { element: step.selector } : {}),
          item: step.item,
          ...(step.advanceOnClick ? { advanceOnClick: step.advanceOnClick } : {}),
          popover: {
            title: eyebrow(recordTitle(step.record)),
            description: recordBody(step.record) + actionStrip(step.actionKey, step.actionTargetKey, step.actionArrow),
            ...(step.side ? { side: step.side } : {}),
            ...(step.align ? { align: step.align } : {}),
            // A coached step waits for the real click; its button only skips it.
            ...(step.advanceOnClick
              ? {
                  showButtons: ['next'],
                  nextBtnText: translate('skipStep', 'tutorial'),
                  popoverClass: 'px-tutorial-popover px-tutorial-coached',
                }
              : {}),
          },
        }));
      if ((!sinceVersion) || (!steps.length)) return steps;
      return [
        {
          popover: {
            title: eyebrow(translate('sectionUpdatedTitle', 'tutorial')),
            description: translate('sectionUpdatedBody', 'tutorial'),
          },
        },
        ...steps,
      ];
    },
    [eligible, translate, recordTitle, recordBody, actionStrip],
  );

  // Silent teardown: null the ref first so the driver's onDestroyed skips onClosed.
  const destroyActiveDriver = useCallback(
    () => {
      const obj = currentDriverRef.current;
      busyRef.current = false;
      setBusyKind(null);
      if (!obj) return;
      currentDriverRef.current = null;
      obj.destroy?.();
    },
    [],
  );

  const runDriver = useCallback(
    (steps, onClosed, startIndex = 0, extraOptions = {}) => {
      // Recover from a driver orphaned by an unmounted anchor, else busyRef dead-locks every play.
      if (busyRef.current) destroyActiveDriver();
      busyRef.current = true;
      setBusyKind((steps.length > 1) ? 'tour' : 'single');
      let completed = false;
      // Tracked live: getActiveIndex() reads 0 once teardown has started.
      let activeIndex = Math.min(Math.max(startIndex, 0), steps.length - 1);
      const seenItems = new Set();
      const { extraButtons, noOutsideClose, ...driverOptions } = extraOptions;
      // driver routes a click on the highlighted element through onNextClick, which
      // cannot see the event: a step may spotlight a block while only one control in
      // it should move on, so the last click is recorded here.
      let lastClickTarget = null;
      const rememberClick = (event) => {
        lastClickTarget = event.target;
      };
      const coached = steps.some((step) => step.advanceOnClick);

      const driverObj = driver({
        showProgress: steps.length > 1,
        progressText: translate(
          'cntOutOfTotal',
          undefined,
          {
            cnt: '{{current}}',
            total: '{{total}}',
          },
        ),
        allowClose: true,
        // The spotlighted element stays highlighted but non-clickable, so the user
        // can't trigger the real button under the popover by mistake — except in a
        // coached tour, where clicking it is the point.
        disableActiveInteraction: !coached,
        popoverClass: 'px-tutorial-popover',
        nextBtnText: translate('next'),
        prevBtnText: translate('previous'),
        doneBtnText: coached ? translate('gotIt', 'tutorial') : translate('done'),
        ...driverOptions,
        // driver closes on its own overlay click too; only the header X and Escape may.
        ...(noOutsideClose ? { overlayClickBehavior: () => {} } : {}),
        steps,
        onHighlighted: () => {
          activeIndex = driverObj.getActiveIndex?.() ?? activeIndex;
          const item = steps[activeIndex]?.item;
          if (item) seenItems.add(item);
        },
        // driver.js exposes no API for extra footer buttons. A wide one goes under
        // the button row rather than beside it.
        onPopoverRender: extraButtons?.length
          ? (popover) => {
              extraButtons.forEach((button) => {
                const el = document.createElement('button');
                el.type = 'button';
                el.textContent = button.label;
                el.className = button.className ?? 'px-tutorial-ghost-btn';
                if (button.title) el.title = button.title;
                el.addEventListener('click', () => button.onClick(driverObj));
                (button.ownLine ? popover.footer : popover.footerButtons).appendChild(el);
              });
            }
          : undefined,
        // Next on the last step completes; so does any close (X, overlay, Esc)
        // while on the last step — the user has seen everything by then.
        onNextClick: () => {
          const wanted = steps[driverObj.getActiveIndex?.() ?? -1]?.advanceOnClick;
          const fromPopover = lastClickTarget?.closest?.('.driver-popover');
          // The skip button always moves on; a click in the spotlight only when it
          // landed on the control the step is actually asking for.
          if (wanted && (!fromPopover) && (!lastClickTarget?.closest?.(wanted))) return;
          if (driverObj.isLastStep()) {
            completed = true;
            driverObj.destroy();
            return;
          }
          // A coached step is advanced by clicking the real control, which is also
          // what mounts what the next step points at: let React commit first.
          const nextElement = steps[(driverObj.getActiveIndex?.() ?? 0) + 1]?.element;
          if (nextElement && (!isVisible(nextElement))) {
            requestAnimationFrame(() => requestAnimationFrame(() => {
              if (currentDriverRef.current === driverObj) driverObj.moveNext();
            }));
            return;
          }
          driverObj.moveNext();
        },
        onDestroyed: () => {
          busyRef.current = false;
          setBusyKind(null);
          clearTimeout(armOutsideClick);
          markExplainedSeen([...seenItems]);
          document.removeEventListener('keydown', onTabKey, true);
          document.removeEventListener('click', rememberClick, true);
          document.removeEventListener('click', onOutsideClick, true);
          // A forced silent teardown nulled the ref first; skip the callback then.
          if (currentDriverRef.current !== driverObj) return;
          currentDriverRef.current = null;
          const finishedOnLastStep = activeIndex >= (steps.length - 1);
          onClosed?.(completed || finishedOnLastStep, activeIndex);
        },
      });
      // driver.js only closes on a click landing on its overlay, and a step that
      // spotlights a large area leaves almost no overlay to hit — while the
      // spotlight itself is inert. So any click outside the popover closes.
      function onOutsideClick(event) {
        if (noOutsideClose) return;
        if (event.target.closest?.('.driver-popover')) return;
        driverObj.destroy();
      }

      // Tab walks a tour's steps; a single explanation leaves Tab to explain mode.
      function onTabKey(event) {
        if ((event.key !== 'Tab') || (steps.length < 2)) return;
        event.preventDefault();
        if (event.shiftKey) {
          driverObj.movePrevious();
        } else if (driverObj.isLastStep()) {
          completed = true;
          driverObj.destroy();
        } else {
          driverObj.moveNext();
        }
      }
      document.addEventListener('keydown', onTabKey, true);
      document.addEventListener('click', rememberClick, true);
      // Next tick, so the click that opened this one does not close it.
      const armOutsideClick = setTimeout(
        () => document.addEventListener('click', onOutsideClick, true),
        0,
      );
      currentDriverRef.current = driverObj;
      driverObj.drive(activeIndex);
      return true;
    },
    [translate, destroyActiveDriver, markExplainedSeen],
  );

  const openGuideAt = useCallback(
    (anchor) => {
      explainOnRef.current = false;
      setExplainOn(false);
      // Use void to make it clear we do not want to wait this async function:
      void navigate(`/guide?to=${encodeURIComponent(anchor)}`);
    },
    [navigate],
  );

  // One explanation, on its own: the page introduction (centred), a first-use
  // explanation or an explain-mode marker. `onClosed(followThrough)` tells a
  // gated action whether to run — reading the guide instead is not a dismissal.
  const showExplanation = useCallback(
    (id, { selector, side, withOptOut, onClosed } = {}) => {
      const record = explainRegistry[id];
      if (!record) return false;
      // Nothing floats unattached: a page or section explanation points at the
      // side-menu entry it describes, and only falls back to a centred popover
      // when that entry is off screen.
      const element = selector ?? (record.menuSelector ? menuAnchor(record) : record.selector);
      let followThrough = true;
      const extraButtons = [];
      if (record.guideAnchor) {
        extraButtons.push({
          label: translate('openGuide', 'tutorial'),
          className: 'px-tutorial-guide-btn',
          onClick: (driverObj) => {
            followThrough = false;
            driverObj.destroy();
            openGuideAt(record.guideAnchor);
          },
        });
      }
      if (withOptOut) {
        extraButtons.push({
          label: translate('optOutLabel', 'tutorial'),
          // What it stops, and that this explanation would not have come back anyway:
          // it describes the button, so it hangs off the button rather than being
          // appended to the explanation, where it would read as a claim about the
          // element being explained.
          title: translate('optOutNote', 'tutorial'),
          className: 'px-tutorial-ghost-btn px-tutorial-optout-btn',
          ownLine: true,
          onClick: (driverObj) => {
            setSkip(true);
            driverObj.destroy();
          },
        });
      }
      const wide = (record.scope === 'page') || (record.scope === 'section');
      const title = wide
        ? '<span class="px-tutorial-release-head">'
          + '<img class="px-tutorial-release-icon" src="/images/svg/gui/infoCircle.svg" alt="">'
          + `<span>${recordTitle(record)}</span>`
          + '</span>'
        : `<span class="px-tutorial-step">${recordTitle(record)}</span>`;
      runDriver(
        [{
          ...(element ? { element } : {}),
          popover: {
            title,
            description: recordBody(record),
            ...(side ? { side } : {}),
            ...((!side) && record.menuSelector ? { side: 'right' } : {}),
          },
        }],
        () => onClosed?.(followThrough),
        0,
        {
          showButtons: ['next'],
          doneBtnText: translate('ok'),
          popoverClass: wide ? 'px-tutorial-popover px-tutorial-page-popover' : 'px-tutorial-popover',
          extraButtons,
        },
      );
      return true;
    },
    [translate, recordTitle, recordBody, runDriver, openGuideAt, setSkip],
  );

  const explainedCount = useCallback(
    () => {
      const persisted = Object.keys(currentOnboarding().explained ?? {});
      const all = new Set([...persisted, ...explainedRef.current]);
      return [...all].filter((id) => (explainRegistry[id]?.scope !== 'page')).length;
    },
    [currentOnboarding],
  );

  // Wraps an action so its explanation shows first, once ever; the action runs when
  // the explanation is dismissed — unless consumeFirst, for a press-and-hold control
  // whose release has long gone by then. Explain mode leaves real controls alone.
  const explainAction = useCallback(
    (id, handler, { consumeFirst = false } = {}) => (...args) => {
      const record = explainRegistry[id];
      const frozen = args.map(freezeArg);
      const run = () => handler?.(...frozen);
      const onItsPage = (!record?.page) || location.pathname.startsWith(`/${record.page}`);
      const gate = record
                    && onItsPage
                    && (!skipRef.current)
                    && (!paxityAccountRef.current)
                    && (!explainOnRef.current)
                    && (!busyRef.current)
                    && (!isExplained(id))
                    && eligible(record)
                    && isVisible(record.selector);
      if (!gate) {
        run();
        return;
      }
      const withOptOut = explainedCount() >= optOutAfterExplanations;
      markExplained(id);
      showExplanation(
        id,
        {
          withOptOut,
          onClosed: (followThrough) => {
            if (followThrough && (!consumeFirst)) run();
          },
        },
      );
    },
    [location.pathname, isExplained, eligible, explainedCount, markExplained, showExplanation],
  );

  // Explain-mode marker click.
  const explainItem = useCallback(
    (id) => {
      const record = explainRegistry[id];
      if ((!record) || busyRef.current) return;
      markExplained(id);
      showExplanation(id);
    },
    [markExplained, showExplanation],
  );

  const setExplainMode = useCallback(
    (on) => {
      // No busy guard: the welcome's last steps have the user switch the mode on
      // while the tour is still playing. Markers sit under driver's overlay.
      explainOnRef.current = on;
      setExplainOn(on);
    },
    [],
  );

  const playSection = useCallback(
    (section, afterDone, startIndex = 0, sinceVersion = 0) => {
      const steps = buildSteps(section, sinceVersion);
      if (!steps.length) {
        afterDone?.(false);
        return;
      }
      runDriver(
        steps,
        (completed) => {
          if (completed) markDone(section);
          afterDone?.(completed);
        },
        startIndex,
        section.noOutsideClose ? { noOutsideClose: true } : {},
      );
    },
    [buildSteps, runDriver, markDone],
  );

  // The messaging concept is the one tour that plays unasked, when the case chat
  // first becomes the user's to use. Governed by the auto setting like the other
  // automatic explanations, and replayable from the tutorials list afterwards.
  const playAutoTour = useCallback(
    (id) => {
      // The caller's condition can hold before the user's settings have landed;
      // acting then would ignore both the auto setting and what is already done.
      if (!onboardingLoadedRef.current) return;
      if (skipRef.current || paxityAccountRef.current || busyRef.current || explainOnRef.current) return;
      // Offered once per session: dismissing leaves it unseen, like any other tour,
      // but the condition that opened it still holds and would reopen it at once.
      if (autoOfferedRef.current.has(id)) return;
      const section = findSection(id);
      if ((!section) || (!eligible(section)) || isDone(section)) return;
      if (!isVisible(section.trigger)) return;
      autoOfferedRef.current.add(id);
      playSection(section);
    },
    [eligible, isDone, playSection],
  );

  // Popup tutorials play only from the popup button (first open / force click).
  // Done only once walked to the end: closed early, it opens again with the popup.
  const playPopupTutorial = useCallback(
    (id, force = false) => {
      const section = findSection(id);
      if ((!section) || (!eligible(section))) return;
      if ((!force) && (skipRef.current || paxityAccountRef.current || isDone(section))) return;
      playSection(section, undefined, 0, force ? 0 : versionSeen(section));
    },
    [eligible, isDone, playSection, versionSeen],
  );

  // Plays over any page, ahead of the arrival popups.
  const playReleaseNotes = useCallback(
    (force = false) => {
      if ((!latestReleaseDate) || (!onboardingLoadedRef.current)) return false;
      if (!force) {
        if (busyRef.current) return false;
        if (releaseSeenRef.current === latestReleaseDate) return false;
      }
      const seen = releaseSeenRef.current;
      const unseen = releaseHighlights.filter((release) => ((!seen) || (release.date > seen)));
      // Asking for it again once everything is read shows the latest release.
      const releases = unseen.length ? unseen : releaseHighlights.slice(0, 1);
      runDriver(
        [{
          popover: {
            // Own flex wrapper: driver.js forces display:block inline on the title.
            title: '<span class="px-tutorial-release-head">'
              + '<img class="px-tutorial-release-icon" src="/images/svg/gui/new2.svg" alt="">'
              + `<span>${translate('whatsNewTitle', 'tutorial')}</span>`
              + '</span>',
            description: releaseNotesHtml(releases, translate, assets.language?.language ?? 'fr'),
          },
        }],
        () => {
          // Marked read on dismissal only, so an unseen release comes back.
          releaseSeenRef.current = latestReleaseDate;
          persistOnboarding({ releaseSeen: latestReleaseDate });
          if (!force) apiRef.current.runArrivals();
        },
        0,
        {
          showButtons: ['next'],
          doneBtnText: translate('ok'),
          popoverClass: 'px-tutorial-popover px-tutorial-release-popover',
        },
      );
      return true;
    },
    [persistOnboarding, runDriver, translate, assets],
  );

  // What a fresh page may open by itself, in order: the release notes, the
  // welcome, then this page's introduction. The auto setting governs only the last.
  const runArrivals = useCallback(
    () => {
      if (busyRef.current || explainOnRef.current) return;
      if (playReleaseNotes()) return;
      if (paxityAccountRef.current) return;
      const welcome = findSection('welcome');
      // Done only once walked to the end. Closed early it comes back, but once per
      // session: the arrivals run on every route change and must not nag.
      if (welcome && eligible(welcome) && (!isDone(welcome)) && isVisible(welcome.trigger)) {
        if (autoOfferedRef.current.has(welcome.id)) return;
        autoOfferedRef.current.add(welcome.id);
        playSection(
          welcome,
          (completed) => {
            if (!completed) return;
            // The page the welcome happened on does not introduce itself right after it.
            Object.entries(explainRegistry)
              .filter(([, record]) => record.seenWithWelcome)
              .forEach(([recordId]) => markExplained(recordId));
            apiRef.current.runArrivals();
          },
          0,
          versionSeen(welcome),
        );
        return;
      }
      if (skipRef.current) return;
      const page = pageRecordFor(location.pathname);
      if ((!page) || isExplained(page.id) || (!eligible(page))) return;
      markExplained(page.id);
      showExplanation(page.id);
    },
    [
      location.pathname, playReleaseNotes, eligible, isDone, isExplained, versionSeen,
      playSection, markExplained, showExplanation,
    ],
  );

  // Navigate to a section's page then play it once the trigger has mounted.
  const replay = useCallback(
    (id) => {
      const section = findSection(id);
      if (!section) return;
      if (location.pathname.startsWith(section.location)) {
        playSection(section);
      } else {
        pendingPlayRef.current = section;
        // Use void to make it clear we do not want to wait this async function:
        void navigate(section.location);
      }
    },
    [location.pathname, navigate, playSection],
  );

  apiRef.current = { runArrivals };

  useEffect(
    () => {
      const requested = pendingPlayRef.current;
      if ((!requested) || (!location.pathname.startsWith(requested.location))) return undefined;
      let tries = 0;
      let timer;
      const attempt = () => {
        if (isVisible(requested.trigger) || (tries >= triggerWaitTries)) {
          pendingPlayRef.current = null;
          playSection(requested);
          return;
        }
        tries += 1;
        timer = setTimeout(attempt, 100);
      };
      attempt();
      return () => clearTimeout(timer);
    },
    [location.pathname, playSection],
  );

  const ready = privilegesReady && onboardingLoaded;

  useEffect(
    () => {
      if (!ready) return undefined;
      const timer = setTimeout(() => apiRef.current.runArrivals(), arrivalDelayMs);
      return () => clearTimeout(timer);
    },
    [ready, location.pathname],
  );

  // Being logged out unmounts the provider, and driver.js renders into
  // document.body: without this an open explanation, its overlay and its key
  // listeners would outlive the dashboard and sit over the login page.
  useEffect(
    () => () => {
      clearTimeout(arrivalTimerRef.current);
      pendingPlayRef.current = null;
      destroyActiveDriver();
    },
    [destroyActiveDriver],
  );

  const sections = useMemo(
    () => (privilegesReady
      ? tutorialRegistry
          .filter(eligible)
          .filter((section) => (!section.popup))
          .map((section) => {
            const done = sessionDone.has(section.id)
            || ((onboarding.tutos?.[section.id] ?? 0) >= section.version);
            return {
              id: section.id,
              pageKey: section.pageKey,
              subPageKey: section.subPageKey,
              subLabelKey: section.subLabelKey,
              subLabelType: section.subLabelType,
              location: section.location,
              labelKey: section.labelKey,
              status: done ? 'done' : 'unseen',
              trigger: section.trigger,
              needsSelection: Boolean(section.needsSelection),
              playHint: section.playHint,
            };
          })
      : []),
    [privilegesReady, sessionDone, onboarding, eligible],
  );

  const contextValue = useMemo(
    () => ({
      tutorial: {
        ready,
        sections,
        skip: Boolean(skip),
        setSkip,
        replay,
        reset,
        playPopupTutorial,
        hasReleaseNotes: Boolean(latestReleaseDate),
        playReleaseNotes,
        endActiveTutorial: destroyActiveDriver,
        playAutoTour,
        explainAction,
        explainItem,
        isExplained,
        eligible,
        explainOn,
        setExplainMode,
        openGuideAt,
        busyKind,
        // Re-render trigger for marker states.
        sessionExplained,
      },
    }),
    [
      ready, sections, skip, setSkip, replay, reset, playPopupTutorial, playReleaseNotes,
      destroyActiveDriver, playAutoTour, explainAction, explainItem, isExplained, eligible, explainOn,
      setExplainMode, openGuideAt, busyKind, sessionExplained,
    ],
  );

  return (
    <TutorialContext.Provider value={contextValue}>
      {children}
    </TutorialContext.Provider>
  );
}

TutorialProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TutorialProvider;
